<template>
  <div v-if="projectRecipients.length">
    <div class="recipient-header">
      <div class="search">
        <div class="search-holder">
          <input
            type="text"
            placeholder="Search by name or email"
            v-model="recipientsSearch"
          />

          <!-- button region -->
          <div class="button-region">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.75 0.999999C13.75 0.585786 13.4142 0.25 13 0.249999L6.25 0.249999C5.83579 0.249999 5.5 0.585786 5.5 0.999999C5.5 1.41421 5.83579 1.75 6.25 1.75H12.25V7.75C12.25 8.16421 12.5858 8.5 13 8.5C13.4142 8.5 13.75 8.16421 13.75 7.75L13.75 0.999999ZM1.53033 13.5303L13.5303 1.53033L12.4697 0.469669L0.46967 12.4697L1.53033 13.5303Z"
                fill="#F5F2EE"
              />
            </svg>
          </div>
        </div>
      </div>
      <div>
        <div class="export-delete">
          <div v-if="projectDetails.state === 'wish_list'">
            <img
              :src="require('../../assets/delete-enabled.svg')"
              :class="{ inactive: !this.selectedRowIds.length }"
              alt="delete"
              @click="isModalActive = true"
            />
          </div>
          <div>
            <button
              :disabled="exporting"
              :class="{ 'button-loading': exporting }"
              @click="exportRecipient"
              class="whitebutton-gemnote button-gemnote-glass--tiny gray"
            >
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <vue-good-table
      :columns="projectRecipientColumns"
      :rows="filteredRecipients"
      :class="{ 'is-editing': this.isEditingRow }"
      styleClass="vgt-table striped"
      :row-style-class="rowStyleClassFn"
      @on-cell-click="onCellClick"
      :search-options="{
        enabled: false
      }"
      :pagination-options="{
        enabled: false,
        mode: 'records',
        perPage: 15,
        perPageDropdown: [10, 15, 20],
        dropdownAllowAll: false,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Rows per page',
        ofLabel: 'of',
        pageLabel: 'page',
        allLabel: 'All'
      }"
    >
      <template slot="table-row" slot-scope="projectRecipients">
        <span v-if="projectRecipients.column.field === 'action'">
          <label class="checkbox-container">
            <input
              v-model="selectedRowIds"
              name="recipient-ids"
              :value="projectRecipients.row.id"
              type="checkbox"
              :disabled="isEditingRow"
            />
            <span
              :class="{
                editing:
                  currentEditRowIndex === projectRecipients.row.originalIndex
              }"
              class="checkmark"
            ></span>
          </label>
        </span>

        <span v-else-if="projectRecipients.column.field === 'name'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.name"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.name }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'email'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.email"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.email }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'phone'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="string"
              class="table-row-input editing"
              @input="handlePhoneUpdate($event)"
              :value="editedRow.phone"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.phone }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'addressOne'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.addressOne"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.addressOne }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'addressTwo'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.addressTwo"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.addressTwo }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'city'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.city"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.city }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'state'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <region-select
              v-model="editedRow.state"
              :country="editedRow.country"
              :placeholder="'State'"
              :className="'select-input'"
              :region="editedRow.state"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.state }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'country'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <country-select
              class="td-country-input"
              v-model="editedRow.country"
              :country="editedRow.country"
              topCountry="US"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.country }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'zipCode'">
          <span
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <input
              type="text"
              class="table-row-input editing"
              v-model="editedRow.zipCode"
            />
          </span>
          <span v-else>
            {{ projectRecipients.row.zipCode }}
          </span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'address'">
          <span class="enter-line">{{
            getFullAddress(projectRecipients.row)
          }}</span>
        </span>

        <span v-else-if="projectRecipients.column.field === 'apparelSize'">
          <template
            v-if="!!Object.keys(projectRecipients.row.apparelSize || {}).length"
          >
            <ul class="product-selection">
              <li
                v-for="(product, index) in Object.keys(
                  projectRecipients.row.apparelSize
                )"
                :key="index"
              >
                {{ productName(product)
                }}<span
                  >({{
                    getSelections(projectRecipients.row.apparelSize[product])
                  }})
                </span>
              </li>
            </ul>
          </template>
        </span>

        <span v-else-if="projectRecipients.column.field === 'trackingUrl'">
          <template v-if="projectRecipients.row.trackingNumbers">
            <span
              class="remove-dashes"
              v-for="(trackingNumber, i) in handleTrackingNumbers(
                projectRecipients.row.trackingNumbers
              )"
              :key="i"
            >
              {{ trackingNumber }}
            </span>
          </template>

          <span v-else>
            Not yet available
          </span>
        </span>

        <div v-else-if="projectRecipients.column.field === 'edit'">
          <div
            v-if="currentEditRowIndex === projectRecipients.row.originalIndex"
          >
            <div class="team-edit-actions">
              <button class="button-gemnote button-gemnote--tiny">
                <span
                  @click.stop="saveEdition(projectRecipients.row.originalIndex)"
                  >Save</span
                >
              </button>
            </div>
          </div>
          <div v-else class="tpeam-edit">
            <div
              class="table-edit-options"
              :class="{ 'edit-button-disabled': isEditingRow }"
            >
              <span>Edit</span>
            </div>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Modal Here -->
    <RemoveRecipientsModal
      :showFooter="true"
      modalName="removeRecipientsModal"
      buttonLabel="Remove"
      colorVariation="secondary"
      v-show="isModalActive"
      @hideModal="isModalActive = false"
      @confirmAction="deleteRecipients"
    >
      <template slot="header">
        <h4 class="editorial header">Remove Recipient(s)</h4>
      </template>
      <template slot="body">
        <p class="modal-description">
          Are you sure you want to remove the selected recipient(s)?
        </p>
      </template>
    </RemoveRecipientsModal>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import { mapState } from 'vuex'

export default {
  name: 'ProjectRecipientsList',
  props: {
    apparelSizeNeeded: {
      type: Boolean,
      required: true,
      default: false
    },
    projectRecipients: {
      type: Array,
      required: true
    },
    projectDetails: {
      type: Object,
      required: true
    }
  },
  components: {
    VueGoodTable,
    RemoveRecipientsModal: () => import('@/components/Universal/RemoveModal')
  },
  data() {
    return {
      cellIndexClicked: new Array(this.projectRecipients.length),
      currentEditRowIndex: null,
      currentUpdatedRowIndex: null,
      isEditingRow: false,
      editedRow: {},
      selectedRowIds: [],
      deletedRowIds: [],
      exporting: false,
      recipientsSearch: '',
      isModalActive: false,
      removeRecipientsModalName: 'removeRecipientsModal',
      stateClass: 'state-col',
      countryClass: 'country-col'
    }
  },

  computed: {
    filteredRecipients() {
      if (this.recipientsSearch) {
        return this.projectRecipients.filter(recipient => {
          const { name } = recipient
          return name
            .toLowerCase()
            .includes(this.recipientsSearch.toLowerCase())
        })
      } else {
        return this.projectRecipients
      }
    },
    projectRecipientColumns() {
      return [
        {
          label: '',
          field: 'action',
          sortable: false,
          width: '5%'
        },
        {
          label: 'Name',
          field: 'name',
          width: '25%'
        },
        {
          label: 'Email',
          field: 'email',
          width: '15%'
        },
        {
          label: 'Phone',
          field: 'phone',
          width: '10%'
        },
        {
          label: 'Address Line 1',
          field: 'addressOne',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'Address Line 2',
          field: 'addressTwo',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'City',
          field: 'city',
          width: '15%',
          hidden: !this.isEditingRow
        },
        {
          label: 'State',
          field: 'state',
          width: '200px',
          hidden: !this.isEditingRow
        },
        {
          label: 'Country',
          field: 'country',
          width: '15%',
          hidden: !this.isEditingRow
        },
        {
          label: 'Zip',
          field: 'zipCode',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'Address',
          field: 'address',
          width: '20%',
          hidden: this.isEditingRow
        },
        {
          label: 'Selections',
          field: 'apparelSize',
          width: '25%',
          hidden: !this.apparelSizeNeeded
        },
        {
          label: 'Tracking',
          field: 'trackingUrl',
          width: '20%',
          hidden: this.isEditingRow
        },
        {
          label: 'Delivery Status',
          field: 'deliveryStatus',
          width: '20%',
          hidden: this.isEditingRow
        },
        {
          label: 'Edit',
          field: 'edit',
          sortable: false,
          width: '100px',
          hidden: !(this.projectDetails.state === 'wish_list')
        }
      ]
    },
    rowEditingClass() {
      if (this.isEditingRow) {
        return 'state-col'
      } else {
        return ''
      }
    },
    ...mapState({
      deletingRecipient: state => state.project.deletingRecipient,
      updatingRecipientInfo: state => state.project.updatingRecipientInfo
    })
  },
  watch: {
    isEditingRow() {
      if (!this.isEditingRow) {
        this.stateClass = ''
        this.countryClass = ''
      }
    }
  },
  methods: {
    handlePhoneUpdate(event) {
      const phone = this.editedRow.phone

      if (!isNaN(Number(event.data))) {
        this.editedRow = { ...this.editedRow, phone: event.target.value }
      } else {
        this.editedRow = { ...this.editedRow, phone: phone }
      }
    },
    getSelections(selection) {
      const selections = Object.values(selection)
        .join(', ')
        .replace(/,/g, ', ')
        .replace(/(^\w{1})|(\s{1}\w{1})/g, match => match.toUpperCase())

      return selections
    },

    productName(name) {
      if (name.length > 9) {
        return name.charAt(0).toUpperCase() + name.slice(1, 9) + '...'
      }
      return name.charAt(0).toUpperCase() + name.slice(1)
    },
    handleTrackingNumbers(trackingNumbers) {
      if (trackingNumbers) {
        return trackingNumbers.split(',')
      }
    },
    getFullAddress(row) {
      if (row.addressTwo) {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.addressTwo ? row.addressTwo : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      } else {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      }
    },
    rowStyleClassFn(row) {
      return row.originalIndex === this.currentUpdatedRowIndex ||
        this.deletedRowIds.includes(row.id)
        ? 'updating-row'
        : 'not-loading'
    },
    onCellClick({ column, row }) {
      switch (column.field) {
        case 'edit':
          if (!this.isEditingRow) {
            this.currentEditRowIndex = row.originalIndex
            this.editedRow = row
            this.isEditingRow = true
          }
          break
      }
    },
    cancelEdition() {},
    async saveEdition() {
      const recipient = { ...this.editedRow }

      recipient.apparelSize = Object.keys(recipient.apparelSize).map(key => {
        return {
          name: key,
          option: recipient.apparelSize[key]
        }
      })

      this.currentUpdatedRowIndex = this.currentEditRowIndex
      this.currentEditRowIndex = null
      this.isEditingRow = false
      await this.$store.dispatch('project/updateRecipient', recipient)
      this.currentUpdatedRowIndex = null
    },

    async deleteRecipients() {
      if (this.selectedRowIds.length) {
        this.deletedRowIds = this.selectedRowIds
        await this.$store.dispatch(
          'project/deleteRecipients',
          this.selectedRowIds
        )
        this.isModalActive = false
        this.deletedRowIds = []
        this.selectedRowIds = []
      }
    },

    async exportRecipient() {
      this.exporting = true
      await this.$store.dispatch('project/exportRecipient', data => {
        const downloadLink = window.document.createElement('a')
        downloadLink.href = data.order_recipient_csv_url
        downloadLink.click()
      })
      this.exporting = false
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';
.remove-dashes {
  text-decoration: none;
}

.header {
  padding-bottom: 0.5rem;
  font-size: 2rem;
  font-weight: 500;
  padding-top: 0.75rem;
  font-family: $editorial;
}

.button-gemnote-glass--tiny {
  font-size: 0.813rem;
  font-weight: normal;
  /* height: 2.5rem; */
  width: auto;
  padding: 0 1rem;
}

.whitebutton-gemnote--small {
  font-size: 0.813rem;
  font-weight: normal;
  height: 2.5rem;
  width: auto;
  padding: 0 1rem;
  margin-right: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.enter-line {
  white-space: pre-line;
}

.apparel-size {
  font-size: $content-font-size;
  display: block;
}

.recipient-header {
  display: flex;
  justify-content: space-between;
  padding: 30px 0;

  .search {
    width: 30%;
  }
}

.search-input {
  padding-bottom: 20px !important;
  background-repeat: no-repeat;
  padding-left: 30px !important;
}

.export-delete {
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    margin-right: 1rem;
    cursor: pointer;

    &.inactive {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}

.table-row-input {
  border: solid 1px #9b9b9b !important;
  font-size: 0.813rem;
  padding: 2px !important;
  height: unset;
  min-width: 100px;
}

.checkbox-container {
  input:disabled ~ .checkmark {
    border-color: rgba(37, 40, 42, 0.5);
  }
}

.edit-button-disabled {
  span {
    color: rgba(80, 80, 80, 0.5) !important;
  }
}

.td-country-input {
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: 180px !important;
  font-size: 0.813rem !important;
}

.country-col {
  padding: 0 !important;
  vertical-align: top;
}
.state-col {
  padding: 0 !important;
  vertical-align: top;
  margin-bottom: 30px;
}
.product-selection {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding-bottom: 12px;
    list-style: disc;
    font-size: 0.875rem;
    line-height: normal;
    justify-content: space-between;
    align-items: center;

    span {
      font-size: 0.75rem;
    }

    span:first-child {
      text-transform: capitalize;
      line-height: unset;
      align-items: flex-start;
    }

    select {
      border: none;
      padding: 0;
      margin: 0;
      width: 84px;
      font-size: 14px;
      height: 52px;
    }

    select::-ms-expand {
      display: block;
    }
  }
}

.search-holder {
  position: relative;
  width: 100%;

  input {
    border: 1px solid #000;
    padding: 0 14px;
    border-radius: 40px;
    font-family: $inter;
    height: 2.5rem;
    font-size: $content-font-size;
    color: $onyx;
    width: 100%;

    &::placeholder {
      text-transform: uppercase;
      font-size: 0.813rem;
      color: #7e7e7e;
      font-weight: 400;
    }

    &:focus,
    &:active {
      border: 1px solid #000 !important;
      outline: none;
    }
  }

  .button-region {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    background: #232220;
    height: 2.5rem;
    border-radius: 0px 40px 40px 0px;
    padding: 0 1rem;
    right: 0;
    top: 0;
  }
}

.select-input {
  font-size: 0.813rem !important;
  position: relative;
  bottom: 2px;
}
.editing {
  margin-top: 12px;
}
@media (max-width: 860px) {
  .recipient-header {
    display: block;
    .search {
      width: 100%;
    }
    .export-delete {
      justify-content: end;
      margin-top: 20px;
    }
  }
}
.modal-description {
  color: $pebble;
  margin-bottom: 3rem;
  font-weight: normal;
}
</style>
